import React, { useEffect, useState } from "react";
import { podcastApi } from "../lib/api";
import { Card, Tile } from "./shared";
import { Mic, RefreshCw, Plus, Trash2, Send, Eye, MailSearch } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (s) => (s ? new Date(s).toLocaleString() : "never");

export function PodcastLpSourcing({ onImported }) {
  const [st, setSt] = useState(null);
  const [job, setJob] = useState(null);
  const [srcs, setSrcs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = () => podcastApi.status().then((s) => { setSt(s); setSrcs(s.sources || []); if (s.lastJob?.status === "running") setJob(s.lastJob); }).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!job || job.status !== "running") return;
    const t = setInterval(async () => {
      try {
        const j = await podcastApi.job(job.id);
        setJob(j);
        if (j.status !== "running") { clearInterval(t); load(); onImported?.(); toast.success(`Podcast sweep done — ${j.added} new Podcast LPs, ${j.recategorised} recategorised`); }
      } catch (e) { clearInterval(t); }
    }, 6000);
    return () => clearInterval(t);
  }, [job?.id, job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveSources = async (next, patch = {}) => {
    setSrcs(next);
    const s = await podcastApi.setConfig({ sources: next, weekly: st.weekly, maxPages: st.maxPages || 60, autoIntro: st.autoIntro, ...patch });
    setSt({ ...st, ...s });
  };
  const intros = async (dryRun) => {
    setBusy(true);
    try {
      const r = await podcastApi.intros({ limit: 20, dryRun });
      setPreview(dryRun ? r.drafts || [] : null);
      toast.success(dryRun ? `Drafted ${(r.drafts || []).length} intro${(r.drafts || []).length === 1 ? "" : "s"} — nothing sent yet`
        : `${r.sent} warm intro${r.sent === 1 ? "" : "s"} sent${r.skipped ? `, ${r.skipped} skipped` : ""}`);
      if (!dryRun) { load(); onImported?.(); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send the intros"); }
    finally { setBusy(false); }
  };
  const chase = async () => {
    setBusy(true);
    try {
      const r = await podcastApi.chaseEmails(20);
      toast.success(`Found ${r.found} email${r.found === 1 ? "" : "s"} of ${r.checked} checked · ${r.remaining} still missing`);
      load(); onImported?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Email chase failed"); }
    finally { setBusy(false); }
  };
  const scrape = async (all) => {
    setBusy(true);
    try {
      const j = await podcastApi.scrape({ all_episodes: all });
      setJob(j);
      toast.success(all ? "Full re-scan started — this takes a few minutes" : "Checking for new episodes — this takes a few minutes");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start the scrape"); }
    finally { setBusy(false); }
  };

  if (!st) return null;
  const running = job?.status === "running";
  return (
    <Card title="Podcast LP auto-sourcing" testid="podcast-sourcing">
      <div className="ra-sub" style={{ marginBottom: 10 }}>
        Crawls each podcast's episode pages (Apify), reads the guest's name, company and contact from the episode notes, and files
        them under <b>Podcast LP</b>. Runs itself weekly and only reads episodes it has not seen before.
      </div>
      <div className="grid g4" style={{ marginBottom: 12 }}>
        <Tile label="Podcast LPs" value={(st.podcastLps || 0).toLocaleString()} tone="go" testid="pl-count" />
        <Tile label="Episodes read" value={(st.episodesSeen || 0).toLocaleString()} sub={`${st.introsSent || 0} intros sent`} testid="pl-episodes" />
        <Tile label="Intros waiting" value={(st.pendingIntros || 0).toLocaleString()} sub={st.autoIntro ? "auto-send after each sweep" : "auto-send off"} tone={st.pendingIntros ? "go" : undefined} testid="pl-pending" />
        <Tile label="Missing email" value={(st.missingEmail || 0).toLocaleString()} tone={st.missingEmail ? "pass" : "go"} sub="guests to chase" testid="pl-missing" />
      </div>

      {srcs.map((s, i) => (
        <div className="tl-edit-row" key={i}>
          <input className="inp mono" value={s} onChange={(e) => setSrcs(srcs.map((x, j) => (j === i ? e.target.value : x)))}
            onBlur={() => saveSources(srcs)} data-testid={`pl-src-${i}`} />
          <button className="icon-btn" onClick={() => saveSources(srcs.filter((_, j) => j !== i))} data-testid={`pl-src-del-${i}`}><Trash2 size={12} /></button>
        </div>
      ))}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
        <button className="btn btn-sm" onClick={() => setSrcs([...srcs, ""])} data-testid="pl-add-src"><Plus size={12} style={{ marginRight: 4 }} />Add podcast</button>
        <button className="btn btn-sm" onClick={() => saveSources(srcs, { weekly: !st.weekly })} data-testid="pl-weekly">
          {st.weekly ? "Weekly refresh: on" : "Weekly refresh: off"}
        </button>
        <button className="btn btn-sm" onClick={() => saveSources(srcs, { autoIntro: !st.autoIntro })} data-testid="pl-autointro">
          {st.autoIntro ? "Auto intro email: on" : "Auto intro email: off"}
        </button>
        <button className="btn btn-share" disabled={busy || running || !st.configured} onClick={() => scrape(false)} data-testid="pl-scrape">
          <Mic size={12} style={{ marginRight: 6 }} />{running ? "Sweeping…" : busy ? "Working…" : "Check for new episodes"}
        </button>
        <button className="btn btn-sm" disabled={busy || running || !st.configured} onClick={() => scrape(true)} data-testid="pl-rescan">
          <RefreshCw size={12} style={{ marginRight: 4 }} />Re-read every episode
        </button>
        <button className="btn btn-sm" disabled={busy || !st.pendingIntros} onClick={() => intros(true)} data-testid="pl-preview-intros">
          <Eye size={12} style={{ marginRight: 4 }} />Preview {st.pendingIntros || 0} intros
        </button>
        <button className="btn btn-sm" disabled={busy || !st.pendingIntros} onClick={() => intros(false)} data-testid="pl-send-intros">
          <Send size={12} style={{ marginRight: 4 }} />Send warm intros
        </button>
        <button className="btn btn-sm" disabled={busy || !st.missingEmail} onClick={chase} data-testid="pl-chase">
          <MailSearch size={12} style={{ marginRight: 4 }} />Chase {Math.min(st.missingEmail || 0, 20)} missing emails
        </button>
      </div>

      {preview?.length > 0 && (
        <div style={{ marginTop: 12 }} data-testid="pl-preview">
          <div className="section-label">Draft intros — nothing has been sent</div>
          {preview.slice(0, 4).map((d, i) => (
            <div className="ir-why-card" key={i} style={{ marginBottom: 8 }} data-testid={`pl-draft-${i}`}>
              <b>{d.name} · {d.to}</b>
              <span><b>{d.subject}</b><br />{d.body}</span>
            </div>
          ))}
        </div>
      )}

      {job && (
        <div className="kv-row" style={{ marginTop: 12 }} data-testid="pl-job">
          <span className="k">{running ? "Sweeping" : "Last sweep"}</span>
          <span className="mono">
            {job.crawled || 0} pages · {job.newEpisodes || 0} new episodes · {job.processed || 0} read ·
            <b className="pos"> +{job.added || 0} added</b> · {job.recategorised || 0} recategorised
            {job.noContact ? ` · ${job.noContact} without contact` : ""}
          </span>
        </div>
      )}
      {job?.errors?.length > 0 && <div className="bullet" style={{ color: "var(--danger)" }}>{job.errors[0]}</div>}
    </Card>
  );
}
